/**
 * Wahoo legacy trainer characteristic (a026e005-…) codec. PROTOCOLS.md §3.
 * Only used when FTMS is absent (old KICKR firmware).
 */
import { clamp } from '@/ble/bytes';

export const WAHOO_OP = {
  UNLOCK: 0x20,
  RESISTANCE: 0x40,
  STANDARD: 0x41,
  ERG: 0x42,
  SIM_INIT: 0x43,
  CRR: 0x44,
  WIND_RESISTANCE: 0x45,
  GRADE: 0x46,
  WIND_SPEED: 0x47,
  WHEEL_CIRCUMFERENCE: 0x48,
} as const;

export function encodeUnlock(): Uint8Array {
  return Uint8Array.of(WAHOO_OP.UNLOCK, 0xee, 0xfc);
}

export function encodeErg(watts: number): Uint8Array {
  const out = new Uint8Array(3);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, WAHOO_OP.ERG);
  dv.setUint16(1, clamp(Math.round(watts), 0, 0xffff), true);
  return out;
}

/** Sim mode init: weight 0.01 kg, crr ×10000, wind resistance coefficient ×1000 (kg/m). */
export function encodeSimInit(weightKg: number, crr: number, windResistanceKgPerM: number): Uint8Array {
  const out = new Uint8Array(7);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, WAHOO_OP.SIM_INIT);
  dv.setUint16(1, clamp(Math.round(weightKg * 100), 0, 0xffff), true);
  dv.setUint16(3, clamp(Math.round(crr * 10000), 0, 0xffff), true);
  dv.setUint16(5, clamp(Math.round(windResistanceKgPerM * 1000), 0, 0xffff), true);
  return out;
}

/** Grade: uint16 (grade_fraction + 1) × 32768. 0 % → 0x8000. */
export function encodeGrade(gradePct: number): Uint8Array {
  const frac = clamp(gradePct / 100, -1, 0.9999);
  const out = new Uint8Array(3);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, WAHOO_OP.GRADE);
  dv.setUint16(1, clamp(Math.round((frac + 1) * 32768), 0, 0xffff), true);
  return out;
}

export function decodeGrade(dv: DataView): number {
  return (dv.getUint16(1, true) / 32768 - 1) * 100;
}

/** Wind speed: uint16 (m/s + 32.768) × 1000. */
export function encodeWindSpeed(mps: number): Uint8Array {
  const out = new Uint8Array(3);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, WAHOO_OP.WIND_SPEED);
  dv.setUint16(1, clamp(Math.round((mps + 32.768) * 1000), 0, 0xffff), true);
  return out;
}

/** Wheel circumference: uint16 in 0.1 mm. */
export function encodeWheelCircumference(meters: number): Uint8Array {
  const out = new Uint8Array(3);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, WAHOO_OP.WHEEL_CIRCUMFERENCE);
  dv.setUint16(1, clamp(Math.round(meters * 10000), 0, 0xffff), true);
  return out;
}

export interface WahooResponse {
  ok: boolean;
  op: number;
}

/** Response indication: `01 <op> …` means success. */
export function parseWahooResponse(dv: DataView): WahooResponse | null {
  if (dv.byteLength < 2) return null;
  return { ok: dv.getUint8(0) === 0x01, op: dv.getUint8(1) };
}
